import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Stack } from "@mui/system";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import Download from "@mui/icons-material/Download";
import FilePresent from "@mui/icons-material/FilePresent";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import UploadFile from "@mui/icons-material/UploadFile";
import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    AttachmentListItemDto,
    RelatedObjectTypeVehicleMaintenance,
} from "../../../models/attachment/attachment";
import {
    getAttachments,
    addAttachments,
    downloadAttachment,
    deleteAttachment,
} from "../../../services/attachmentService";
import { LoadingIndicator } from "../../framework/loadingIndicatorNew";
import { AppUtils } from "../../../models/common/appUtils";
import { ConfirmationDialogResult } from "../../../models/common/enums";
import mergeFileLists from "../../../models/common/mergeFileLists";

const fileSizeLimit = 1024000 * 10; // 10 MB limit
const acceptedFileTypes = "image/png, image/jpeg, application/pdf";

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
});

interface IMaintenanceAttachmentsPanelProps {
    maintenanceId: string;
    onClose: () => void;
}

export const MaintenanceAttachmentsPanel = ({
    maintenanceId,
    onClose,
}: IMaintenanceAttachmentsPanelProps) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [fileFormData, setFileFormData] = useState<FormData>(null);
    const [files, setFiles] = useState<FileList>(null);
    const [fileNames, setFileNames] = useState<string[]>(null);
    const [attachmentFiles, setAttachmentFiles] =
        useState<AttachmentListItemDto[]>(null);
    const [hasInvalidFiles, setHasInvalidFiles] = useState(false);

    const fetchAttachmentFiles = useCallback(async() => {
        setLoading(true);
        try {
            const res = await getAttachments({
                relatedObjectType: RelatedObjectTypeVehicleMaintenance,
                relatedObjectId: maintenanceId,
            });
            if (res?.data) {
                setAttachmentFiles(res.data);
            }
        } finally {
            setLoading(false);
        }
    }, [maintenanceId]);

    useEffect(() => {
        if (maintenanceId) {
            void fetchAttachmentFiles();
        }
    }, [maintenanceId, fetchAttachmentFiles]);

    const handleFileAdd = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) {
            return;
        }
        setHasInvalidFiles(false);

        const formData = new FormData();
        const newFileNames = [];

        let newFileList = e.target.files;
        if (files?.length > 0) {
            newFileList = mergeFileLists(files, e.target.files);
        }

        let i = 0;
        while (i < newFileList.length) {
            if (
                newFileList[i].size < fileSizeLimit &&
                acceptedFileTypes.includes(newFileList[i].type)
            ) {
                const file = newFileList[i];
                newFileNames.push(file.name);
                formData.append("files", file);
            } else {
                setHasInvalidFiles(true);
            }
            i++;
        }
        setFiles(newFileList);
        setFileNames(newFileNames);
        setFileFormData(formData);
    }, [files]);

    const handleFileUpload = useCallback(() => {
        addAttachments(fileFormData, {
            relatedObjectType: RelatedObjectTypeVehicleMaintenance,
            relatedObjectId: maintenanceId,
        })
            .then(() => {
                void fetchAttachmentFiles();
                setHasInvalidFiles(false);
                setFiles(null);
                setFileNames(null);
                setFileFormData(null);
            })
            .catch((e) => {
                console.error(e);
            })
            .finally(() => {
                setIsUploading(false);
            });
    }, [fileFormData, maintenanceId, fetchAttachmentFiles]);

    const handleFileUploadAction = useCallback(() => {
        setIsUploading(true);
        void handleFileUpload();
    }, [handleFileUpload]);

    const handleFilesToUploadDelete = (fileIndex: number) => {
        const updatedFiles = new DataTransfer();
        const formData = new FormData();
        const names = [];
        let i = 0;
        while (i < files.length) {
            if (i !== fileIndex) {
                const file = files[i];
                updatedFiles.items.add(file);
                names.push(file.name);
                formData.append("files", file);
            }
            i++;
        }
        setFiles(updatedFiles.files);
        setFileNames(names);
        setFileFormData(formData);
    };

    const handleFileDownload = (file: AttachmentListItemDto) => {
        downloadAttachment(file.attachmentId, {
            relatedObjectType: RelatedObjectTypeVehicleMaintenance,
            relatedObjectId: maintenanceId
        })
            .then((res) => {
                const data = new Blob([res.data], { type: file.fileType });
                const fileURL = window.URL.createObjectURL(data);
                const tempLink = document.createElement("a");
                tempLink.href = fileURL;
                tempLink.setAttribute("download", file.fileName);
                tempLink.click();
            })
            .catch((e) => {
                console.error(e);
            });
    };

    const handleFileDelete = async(attachmentId: string, fileName: string) => {
        const confirmationResult = await AppUtils.showConfirmationDialog(
            t("vehicleMaintenance.deleteAttachment", { fileName: fileName })
        );
        if (confirmationResult !== ConfirmationDialogResult.Yes) return;

        void deleteAttachment(attachmentId)
            .then(() => {
                void fetchAttachmentFiles();
            })
            .catch((e) => {
                console.error(e);
            });
    };

    return (
        <Stack
            spacing={2}
            sx={{ width: "100%" }}
            fontSize="small"
            paddingBottom={2}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
            >
                <Typography variant="h4">{t("common.attachments")}</Typography>
                <Box>
                    <IconButton
                        className="close-panel-button"
                        size="small"
                        onClick={onClose}
                    >
                        <CloseIcon color="primary" />
                    </IconButton>
                </Box>
            </Stack>
            <Box>
                {loading ? (
                    <LoadingIndicator />
                ) : (
                    <>
                        {attachmentFiles && attachmentFiles.length > 0 ? (
                            <List>
                                {attachmentFiles.map((file) => (
                                    <ListItem
                                        key={file.attachmentId}
                                        disablePadding
                                        secondaryAction={
                                            <>
                                                <IconButton
                                                    onClick={() =>
                                                        void handleFileDownload(
                                                            file
                                                        )
                                                    }
                                                    edge="end"
                                                    aria-label="download"
                                                    sx={{ marginRight: 0 }}
                                                >
                                                    <Download color="primary" />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() =>
                                                        void handleFileDelete(
                                                            file.attachmentId,
                                                            file.fileName
                                                        )
                                                    }
                                                    edge="end"
                                                    aria-label="delete"
                                                >
                                                    <DeleteIcon color="error" />
                                                </IconButton>
                                            </>
                                        }
                                    >
                                        <ListItemIcon
                                            style={{ minWidth: "38px" }}
                                        >
                                            <FilePresent />
                                        </ListItemIcon>
                                        <ListItemText
                                            disableTypography
                                            primary={
                                                <Tooltip
                                                    title={file.fileName}
                                                    placement="bottom"
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        className="text-truncate"
                                                        style={{
                                                            maxWidth:
                                                                "calc(100% - 90px)",
                                                        }}
                                                    >
                                                        {file.fileName}
                                                    </Typography>
                                                </Tooltip>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2">
                                {t("vehicleMaintenance.noAttachments")}
                            </Typography>
                        )}
                    </>
                )}
            </Box>
            <Box>
                <Typography color="primary.dark" variant="h4">
                    {t("vehicleMaintenance.addAttachments")}
                </Typography>
                <Box>
                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <InfoOutlined />
                            </ListItemIcon>
                            <ListItemText
                                primary={t("common.allowedFileFormats", {
                                    formats: "JPG, PNG, PDF",
                                })}
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <InfoOutlined />
                            </ListItemIcon>
                            <ListItemText
                                primary={t("common.maxFileSize", {
                                    size: "10 MB",
                                })}
                            />
                        </ListItem>
                    </List>
                </Box>
                <Button
                    component="label"
                    role={undefined}
                    variant="outlined"
                    tabIndex={-1}
                    startIcon={<UploadFile />}
                >
                    {t("common.addAttachments")}
                    <VisuallyHiddenInput
                        accept={acceptedFileTypes}
                        type="file"
                        onChange={handleFileAdd}
                        multiple
                    />
                </Button>
                {fileNames && fileNames.length > 0 && (
                    <Box>
                        <List sx={{ paddingY: 2 }}>
                            {fileNames.map((file, index) => (
                                <ListItem
                                    key={index}
                                    disablePadding
                                    secondaryAction={
                                        <IconButton
                                            onClick={() =>
                                                void handleFilesToUploadDelete(
                                                    index
                                                )
                                            }
                                            edge="end"
                                            aria-label="delete"
                                        >
                                            <DeleteIcon color="error" />
                                        </IconButton>
                                    }
                                >
                                    <ListItemIcon>
                                        <FilePresent />
                                    </ListItemIcon>
                                    <ListItemText primary={file} />
                                </ListItem>
                            ))}
                        </List>
                        <Button
                            color="success"
                            disabled={isUploading}
                            variant="contained"
                            onClick={() => void handleFileUploadAction()}
                        >
                            {t("common.save")}
                        </Button>
                    </Box>
                )}
                {hasInvalidFiles && (
                    <Box marginTop={2}>
                        <Alert
                            severity="error"
                            onClose={() => {
                                setHasInvalidFiles(false);
                            }}
                        >
                            {t("common.attachmentFileInvalid")}
                        </Alert>
                    </Box>
                )}
            </Box>
        </Stack>
    );
};
